import { ErrorHandler, Injectable } from '@angular/core';

// Browser messages for a lazy-loaded file (route module, PDF viewer, ...) that could not be loaded:
// Chrome/Edge, Firefox and Safari.
const CHUNK_LOAD_ERRORS = [
  'Failed to fetch dynamically imported module',
  'error loading dynamically imported module',
  'Importing a module script failed'
];
const RELOADED_AT_KEY = 'chunkLoadErrorReloadedAt';
const RELOAD_WINDOW_MS = 60_000;

// After a deployment, a tab still running the previous build asks for lazy-loaded files that no longer exist (their
// names change with every build) and the server answers with index.html, so the import fails. Reload the page once
// to pick up the new build. If the error comes back within a minute of that reload, the build itself is broken:
// report it as usual instead of reloading in a loop. Every other error goes to Angular's default handler.
@Injectable()
export class ChunkLoadErrorHandler extends ErrorHandler {

  override handleError(error: any): void {
    if (isChunkLoadError(error) && canReload()) {
      window.location.reload();
      return;
    }
    super.handleError(error);
  }
}

function isChunkLoadError(error: any): boolean {
  // Errors from promises (e.g. a lazy route) can arrive wrapped in a zone rejection
  const message = String(error?.rejection?.message ?? error?.message ?? error ?? '');
  return CHUNK_LOAD_ERRORS.some(text => message.includes(text));
}

function canReload(): boolean {
  try {
    const reloadedAt = Number(sessionStorage.getItem(RELOADED_AT_KEY));
    if (reloadedAt && Date.now() - reloadedAt < RELOAD_WINDOW_MS) {
      return false;
    }
    sessionStorage.setItem(RELOADED_AT_KEY, String(Date.now()));
    return true;
  } catch {
    return false; // storage blocked: without the loop guard, don't reload
  }
}
